import { useMemo, useRef, useState } from "react";

import type {
  DateRangeValue,
  DecodedArrayValues,
} from "../../context/urlParams";
import type { PerformanceReportParams } from "../providers/PerformanceReportContext";
import type { PerformanceReportInitialState } from "../providers/PerformanceReportProvider";
import type {
  PerformanceReportSelectOptions,
  SourceType,
  ViewTab,
} from "../types";
import type { ArrayParamKey } from "./schema";

import {
  decodeArrayParams,
  decodeDateRange,
  encodeSearch,
} from "../../context/urlParams";
import {
  ARRAY_PARAM_KEYS,
  buildArrayParamWhitelists,
  clampPerformanceReportRange,
  decodeViewTab,
  defaultDateRange,
  hasArrayFilterParams,
} from "./schema";

export type DecodedPerformanceReportParams = {
  values: DecodedArrayValues<ArrayParamKey>;
  fullyInvalidFacets: ArrayParamKey[];
  range: DateRangeValue;
  tab: ViewTab | undefined;
};

export type UsePerformanceReportUrlParamsInput = {
  /** The current route search, already read by the caller (each app owns its
   * own `useSearch` call, keeping this hook framework-agnostic). */
  search: Record<string, string | undefined>;
  /** The page's resolved option sets, used to whitelist facet values. */
  selectOptions: PerformanceReportSelectOptions;
  /**
   * True while the filter-options request for a deep link is in flight. A
   * filtered deep link stays undecoded (and the `providerKey` latch stays
   * pending) until this settles, so the provider is only seeded once the option
   * sets it validates against are actually available.
   */
  isOptionsLoading: boolean;
  /**
   * The deep link's options fetch failed (e.g. the app's request returned no
   * data). When true, decoding and URL writes are skipped so the provider
   * falls back to its own defaults rather than seeding from unvalidated params.
   */
  optionsError: boolean;
  /** Writes the encoded search to the URL. Each app supplies its own typed
   * `navigate` call (route + params), keeping this hook route-agnostic. */
  writeSearch: (encoded: Record<string, string>) => void;
  /** Extra search keys to carry through every in-page URL rewrite (e.g. the
   * portal's `from` origin). Read from the current search and re-attached. */
  preserveSearchKeys?: readonly string[];
};

export type UsePerformanceReportUrlParamsResult = {
  hasUrlFilters: boolean;
  hasUrlParams: boolean;
  decoded: DecodedPerformanceReportParams | undefined;
  initialState: PerformanceReportInitialState | undefined;
  providerKey: string;
  handleParamsChange: (params: PerformanceReportParams) => void;
};

const buildInitialState = (
  decoded: DecodedPerformanceReportParams,
): PerformanceReportInitialState => ({
  dateRange: decoded.range,
  selected: {
    ...(decoded.tab && { viewTab: decoded.tab }),
    ...(decoded.values.sourceType?.length && {
      sourceType: decoded.values.sourceType as SourceType[],
    }),
    ...(decoded.values.landingPage?.length && {
      landingPage: decoded.values.landingPage,
    }),
    ...(decoded.values.bannerGroup?.length && {
      bannerGroup: decoded.values.bannerGroup,
    }),
    ...(decoded.values.rebateProgram?.length && {
      rebateProgram: decoded.values.rebateProgram,
    }),
    ...(decoded.values.country?.length && {
      country: decoded.values.country,
    }),
    ...(decoded.values.campaign?.length && {
      campaign: decoded.values.campaign,
    }),
  },
});

/**
 * Route-agnostic core of the Performance Report's URL-param deep-linking,
 * shared by portal and backoffice. It decodes and whitelists the search into a
 * provider `initialState`, exposes a stable `providerKey` so later URL writes
 * don't remount the provider, and returns a `handleParamsChange` that keeps the
 * URL in sync. App-specific concerns — how filter options are fetched, campaign
 * options, and the portal's origin/back-link — stay in the caller; this hook
 * only receives the resolved `selectOptions` and a `writeSearch` callback.
 */
export const usePerformanceReportUrlParams = ({
  search,
  selectOptions,
  isOptionsLoading,
  optionsError,
  writeSearch,
  preserveSearchKeys,
}: UsePerformanceReportUrlParamsInput): UsePerformanceReportUrlParamsResult => {
  const hasUrlFilters = hasArrayFilterParams(search);

  // `from` (and any other non-facet origin param) is deliberately excluded: a
  // URL carrying only such a param has no filters to validate and must behave
  // like an un-deep-linked visit.
  const hasUrlParams =
    hasUrlFilters ||
    Boolean(search.startDate) ||
    Boolean(search.endDate) ||
    Boolean(search.tab);

  // A filtered deep link can't be decoded until its options resolve (facet
  // values must be whitelisted against them); a dates/tab-only link needs no
  // options and decodes immediately. Keeping `decoded` undefined while options
  // load leaves the `providerKey` latch pending, so a fetch that first fails
  // and later succeeds (e.g. on filter open) can still apply the deep link.
  const awaitingOptions = hasUrlFilters && isOptionsLoading;

  const decoded = useMemo((): DecodedPerformanceReportParams | undefined => {
    if (!hasUrlParams || optionsError || awaitingOptions) return undefined;
    const whitelists = buildArrayParamWhitelists(selectOptions);
    const { values, fullyInvalidFacets } = decodeArrayParams(
      search,
      whitelists,
    );
    const { range } = decodeDateRange(search, defaultDateRange());
    const tab = decodeViewTab(search.tab);
    return {
      values,
      fullyInvalidFacets,
      range: clampPerformanceReportRange(range),
      tab,
    };
  }, [hasUrlParams, optionsError, awaitingOptions, search, selectOptions]);

  const initialState = decoded ? buildInitialState(decoded) : undefined;

  const lastWrittenSearchRef = useRef<string | null>(
    hasUrlParams
      ? null
      : JSON.stringify(
          encodeSearch(
            Object.fromEntries(ARRAY_PARAM_KEYS.map((key) => [key, null])),
            defaultDateRange(),
          ),
        ),
  );

  const handleParamsChange = (params: PerformanceReportParams) => {
    if (optionsError) return;
    const arrayValues = Object.fromEntries(
      ARRAY_PARAM_KEYS.map((key) => [key, params.selected[key]]),
    );
    const encoded = encodeSearch(arrayValues, {
      from: params.dateRange.startDate,
      to: params.dateRange.endDate,
    });
    // omit the default tab so an un-deep-linked visit keeps a clean URL
    if (params.tab !== "source") {
      encoded.tab = params.tab;
    }
    // Re-attach preserved keys (e.g. the portal's `from`): `encodeSearch` only
    // emits array facets + dates, so without this an in-page URL rewrite would
    // silently drop them on the first interaction.
    for (const key of preserveSearchKeys ?? []) {
      const value = search[key];
      if (value) encoded[key] = value;
    }
    const nextKey = JSON.stringify(encoded);
    if (lastWrittenSearchRef.current === nextKey) return;
    lastWrittenSearchRef.current = nextKey;
    writeSearch(encoded);
  };

  // Latched once the seed resolves, so a later URL write (e.g. a tab switch)
  // can't flip this and force an unwanted remount that resets provider state.
  // Left unlatched while `decoded` is still pending so a deep link whose options
  // fetch initially fails can still pick up a later successful decode.
  const liveKey = hasUrlParams
    ? decoded
      ? "url-params-decoded"
      : "url-params-pending"
    : "no-url-params";
  const seedResolved = !hasUrlParams || Boolean(decoded);
  // Refs can't be read/written during render (React Compiler's react(refs)
  // rule) — latch via the "adjust state during render" pattern instead:
  // setState called conditionally, guarded so it only fires once, is the
  // React-sanctioned way to derive state from a render-time condition without
  // an effect (https://react.dev/learn/you-might-not-need-an-effect).
  const [latchedKey, setLatchedKey] = useState<string | null>(null);
  if (latchedKey === null && seedResolved) {
    setLatchedKey(liveKey);
  }
  const providerKey = latchedKey ?? liveKey;

  return {
    hasUrlFilters,
    hasUrlParams,
    decoded,
    initialState,
    providerKey,
    handleParamsChange,
  };
};
